import { computed, action, observable, reaction, makeObservable } from 'mobx'
import get from 'lodash/get'
import isPlainObject from 'lodash/isPlainObject'

import Settings from '@client/utils/Settings'
import ColumnModel from '@client/components/DroppableWrapper/model'
import {
  defaultSettings,
  defaultSettingsByType,
  defaultSettingsByFormType,
  ITEM_TYPE_FORM,
  ITEM_TYPE_MAIL,
  ITEM_TYPE_CONFIRMATION_MAIL,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_RESPONDER,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
  ITEM_TYPE_SURVEY,
  ITEM_TYPE_SURVEY_THANK_YOU,
  WIZARD_TYPE_PAGE,
  WIZARD_TYPE_FORM,
  ITEM_TYPE_FORM_SUBTYPE_SUCCESS,
} from './settings'
import { ganApi } from '@client/api'
import storage from '@client/utils/Storage'
import { STATIC_ATTRIBUTES } from '@client/components/blocks/Text/plugins/constants'
import { hash } from '@client/utils/hash'
import {
  fingerprint,
  isNewerServerVersion,
  matchesLocal,
} from '@client/utils/remoteVersion'
import { SAVE_ERROR_KINDS, describeSaveError } from '@client/utils/saveError'
import { MAIL_TYPE_UPDATED } from '@client/components/constants'
import { webSafeFonts } from './settings'
import { BLOCK_TYPES } from '@client/components/Block/constants'
import { FORMCOL_SECTION_BG_IMAGE_POSITION_TYPES } from '@client/components/Section/constants'
import { getServerFieldType } from '../Block/constants'
import createBlockModel from '@client/components/Block/createBlockModel'
import {
  SECTION_LAYOUTS,
  SECTION_TYPES,
} from '@client/components/Section/constants'
import { captureException } from '@client/utils/sentry'
import { extractLinks, classifyLink } from '@client/utils/linkCheck'
import { rgbObjectToHex, hexToRGBA } from '@client/utils/rgbToHex'

export const defaultSubmitButton = {
  type: BLOCK_TYPES.SUBMIT_BUTTON,
  settings: {
    text: 'survey.button.submit.text',
  },
}

export const defaultEmptySurvey = {
  items: [
    {
      type: BLOCK_TYPES.SECTION,
      sectionType: SECTION_TYPES.ONECOL,
      columns: [
        {
          items: [defaultSubmitButton],
        },
      ],
      settings: {
        layout: SECTION_LAYOUTS[1].value,
      },
    },
  ],
}

const VIEW_IN_BROWSER_MARKUP = `##${STATIC_ATTRIBUTES[0].code}##`
const UNSUBSCRIBE_MARKUP = `##${STATIC_ATTRIBUTES[1].code}##`
const CONFIRM_MARKUP = `##${STATIC_ATTRIBUTES[6].code}##`

class EditorModel {
  @observable kind = ''
  @observable subKind = ''
  @observable wizardKind = ''
  @observable itemHash = undefined
  @observable subject = ''
  @observable url = ''
  @observable status = ''
  @observable isSaving = false
  @observable isValid = true
  @observable versionHash = ''
  // Fingerprint of the server's copy as of the last load or save. `versionHash`
  // above is the LOCAL dirty check — these two answer different questions and
  // must not be conflated. See FE-1354.
  @observable remoteFingerprint = ''
  // Set when a background poll finds a newer server copy but this tab has
  // unsaved edits, so it cannot be swapped in. Purely informational.
  @observable remoteChangePending = false
  @observable remoteChangeDismissed = false
  // The server version we have told the user about. Distinct from
  // `remoteFingerprint`, which is the version this document actually reflects:
  // warning about a version is not adopting it, and conflating the two leaves a
  // tab that returns to clean believing it is current when it is not.
  @observable remoteChangeFingerprint = ''
  // Why the last save was rejected (FE-1431), or null. `versionHash` is the
  // document that was rejected, so autosave can wait for the user to change
  // it before sending it again.
  @observable saveError = null
  // Whether the last save attempt failed for any reason, including a 5xx or a
  // dropped connection, which leave saveError null and open the reconnect
  // dialog instead. Callers that navigate away check this, so they don't leave
  // with edits that only exist in this tab (FE-1431).
  @observable lastSaveFailed = false
  // The server's `updated` for the version this tab holds, as of the last load,
  // save or reload. Checked before every save so a tab does not overwrite a
  // newer version saved somewhere else. See FE-1441.
  @observable serverUpdated = ''
  // The newer server copy that blocked the last save, until the user loads it.
  // Saving waits until they do.
  @observable saveConflict = null
  // The document as it stood when the user first did something in this tab
  // (or as last saved), or null before that. Blocks rewrite their own
  // settings after mounting (a button fits its width to the column, default
  // text keys get translated), which leaves a freshly loaded document unsaved
  // although the user changed nothing. Only changes from this point on are the
  // user's. See FE-1485.
  userStartHash = null
  @observable shouldShowAds = false
  @observable columns = []
  @observable settings = undefined
  @observable questions = undefined
  @observable error = undefined
  @observable viewMode = 'desktop'
  @observable linkIssuesPanelOpen = false

  @action setLinkIssuesPanelOpen = (val) => {
    this.linkIssuesPanelOpen = val
  }

  @action toggleLinkIssuesPanel = () => {
    this.linkIssuesPanelOpen = !this.linkIssuesPanelOpen
  }

  constructor(item = {}, subKind = '') {
    this.load(item, subKind)

    makeObservable(this)

    reaction(
      () => this.settings.values,
      (values) => {
        this.globalSettingsStyles.updateCSS(values)
      }
    )
  }

  getBodyFieldName = () => {
    if (this.kind === ITEM_TYPE_CONFIRMATION_MAIL) {
      return 'mail_body'
    }
    if (
      this.kind === ITEM_TYPE_LANDING_PAGE ||
      this.kind === ITEM_TYPE_SURVEY
    ) {
      return 'form_body'
    }
    if (this.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS) {
      return 'success_page_body'
    }
    if (this.kind === ITEM_TYPE_SURVEY_THANK_YOU) {
      return 'thank_you_page_body'
    }
    if (this.kind && this.itemHash) {
      return `${`${this.kind}`.toLowerCase()}_body`
    }
    return 'body'
  }

  @action load = (item, subKind) => {
    // Before anything else: this method parses `item.body` from a JSON string
    // into an object in place, so the baseline has to be taken while the item
    // still looks the way the API returned it.
    this.setRemoteFingerprint(fingerprint(item))
    this.setServerUpdated(item && item.updated)
    // A different document carries none of the previous one's warnings.
    this.clearRemoteChange()
    this.clearSaveError()
    this.setLastSaveFailed(false)
    this.clearSaveConflict()

    this.kind = item.kind || ITEM_TYPE_MAIL
    this.subKind = subKind || ''
    this.wizardKind = item.wizardKind || ''
    this.id = item.id
    this.url = item.url
    this.itemHash = item.hash
    this.status = item.status
    this.viewMode = 'desktop'

    const bodyFieldName = this.getBodyFieldName()

    if (item && item.id && item.body && item.body.replace) {
      try {
        item.body = JSON.parse(item.body)
      } catch (e) {
        item.body = {}
      }
    }
    if (this.id && (!item.body || (item.body && !item.body.settings))) {
      item.body = {}
    }
    if (
      item &&
      item.hash &&
      item.kind &&
      item[bodyFieldName] &&
      item[bodyFieldName].replace
    ) {
      try {
        item[bodyFieldName] = JSON.parse(item[bodyFieldName])
      } catch (e) {
        item[bodyFieldName] = {}
      }
    }
    if (
      this.itemHash &&
      this.kind &&
      (!item[bodyFieldName] ||
        (item[bodyFieldName] && !item[bodyFieldName].settings))
    ) {
      item[bodyFieldName] = {}
    }

    let designSettings = {}
    if (isPlainObject(item.design_settings)) {
      designSettings = item.design_settings
    } else {
      try {
        designSettings = JSON.parse(item.design_settings)
      } catch (e) {
        designSettings = {}
      }
    }

    this.form_type = item.form_type
    if (
      this.wizardKind === WIZARD_TYPE_FORM &&
      (this.kind === ITEM_TYPE_FORM ||
        this.kind === ITEM_TYPE_FORM_SUBTYPE_SUCCESS) &&
      !this.form_type
    ) {
      this.form_type = 'popup'
    }

    if (!this.settings) {
      this.settings = new Settings({
        ...defaultSettings,
        ...(defaultSettingsByType[this.kind] || {}),
        ...(defaultSettingsByFormType[this.form_type] || {}),
      })
    } else {
      this.settings.setDefault({
        ...defaultSettings,
        ...(defaultSettingsByType[this.kind] || {}),
        ...(defaultSettingsByFormType[this.form_type] || {}),
      })
    }
    if (this.id && item.body) {
      this.settings.set(item.body.settings || {})
    }
    if (this.itemHash && this.kind && item[bodyFieldName]) {
      this.settings.set({
        ...(item[bodyFieldName].settings || {}),
        ...designSettings,
      })
    }

    if (this.id && item.body) {
      this.columns = [
        new ColumnModel(
          (item &&
            item.body &&
            item.body.columns &&
            item.body.columns.length &&
            item.body.columns[0]) ||
            [],
          this
        ),
      ]
    } else if (this.itemHash && this.kind && item[bodyFieldName]) {
      this.columns = [
        new ColumnModel(
          (item &&
            item[bodyFieldName] &&
            item[bodyFieldName].columns &&
            item[bodyFieldName].columns.length &&
            item[bodyFieldName].columns[0]) ||
            [],
          this
        ),
      ]
    } else {
      this.columns = []
    }
    if (this.kind === ITEM_TYPE_SURVEY && !this.columns.length) {
      this.columns = [new ColumnModel(defaultEmptySurvey, this)]
    }
    if (
      this.kind === ITEM_TYPE_SURVEY &&
      this.columns.length &&
      this.columns[0] &&
      !this.columns[0].items.length
    ) {
      this.columns = [new ColumnModel(defaultEmptySurvey, this)]
    }
    if (this.kind === ITEM_TYPE_SURVEY && !this.hasSubmitButton) {
      const column = get(this.columns, '[0].items[0].columns[0]')
      if (column) {
        column.addItemAtTheEnd(createBlockModel(defaultSubmitButton, this))
      }
    }

    this.language = 'en'
    if (item.language) {
      this.language = item.language || 'en'
    }

    if (item.thumbnail_url) {
      this.thumbnailUrl = item.thumbnail_url
    }

    if (item.preview_url) {
      this.previewUrl = item.preview_url
    }

    if (typeof window === 'object') {
      const GlobalCSS = require('../../utils/GlobalCSS').default
      this.globalSettingsStyles = new GlobalCSS()
      this.globalSettingsStyles.updateCSS(this.settings.values)
    }

    this.form_delay = item.form_delay
    this.form_page_filter = item.form_page_filter
    this.form_on_scroll = item.form_on_scroll
    this.form_trigger = item.form_trigger

    this.amendSubject(item)

    this.setIsSaving(false)
    this.setShouldShowAds(!!item.should_show_ads)

    this.setVersionHash(hash(this.serialize()))
    this.userStartHash = null
  }

  @action amendSubject = (item) => {
    this.name = item.name
    this.list = item.list
    this.sender = item.sender
    this.status = item.status
    this.template = item.template

    this.form_delay = item.form_delay
    this.form_page_filter = item.form_page_filter

    this.form_page_title = item.form_page_title
    this.success_page_title = item.success_page_title

    this.page_title = item.page_title

    this.mail_subject = item.mail_subject
    this.setPreHeader(item.pre_header)

    if (this.id) {
      this.setSubject(item.subject || '')
    }
    if (this.itemHash && this.kind === ITEM_TYPE_CONFIRMATION_MAIL) {
      this.setSubject(this.mail_subject || '')
    }
    if (this.itemHash && this.kind === ITEM_TYPE_PAGE) {
      this.setSubject(this.page_title || '')
    }
    if (this.itemHash && this.kind === ITEM_TYPE_LANDING_PAGE) {
      this.setSubject(this.form_page_title || '')
    }
    if (this.itemHash && this.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS) {
      this.setSubject(this.success_page_title || '')
    }
    if (this.itemHash && this.kind === ITEM_TYPE_SURVEY) {
      this.setSubject(this.name || '')
    }
    if (this.itemHash && this.kind === ITEM_TYPE_SURVEY_THANK_YOU) {
      this.setSubject(this.name || '')
    }
  }

  @action amend = (item) => {
    if (item && item.id && item.body && item.body.replace) {
      try {
        item.body = JSON.parse(item.body)
      } catch (e) {
        console.error(e)
        item.body = {}
      }
      if (this.columns && this.columns.length && this.columns[0]) {
        this.columns[0].amend(item.body.columns[0].items)
      }
      this.settings.set(item.body.settings)
    }

    const bodyFieldName = this.getBodyFieldName()

    if (
      item &&
      item.hash &&
      item.kind &&
      item[bodyFieldName] &&
      item[bodyFieldName].replace
    ) {
      try {
        item[bodyFieldName] = JSON.parse(item[bodyFieldName])
      } catch (e) {
        console.error(e)
        item[bodyFieldName] = {}
      }
      if (this.columns && this.columns.length && this.columns[0]) {
        this.columns[0].amend(item[bodyFieldName].columns[0].items)
      }
      this.settings.set(item[bodyFieldName].settings)
    }
    this.amendSubject(item)
  }

  /**
   * Replaces the open document with the server's copy, in place.
   *
   * Uses amend() rather than load() deliberately. load() is a startup path: it
   * constructs a fresh GlobalCSS whose constructor appends a <style> element to
   * <head> and orphans the previous one, and it replaces columns[0], which
   * silently wipes the root column's `acceptTypes` (written only by
   * DroppableWrapper's constructor, and absent from the serialized body) so
   * sections can no longer be dropped onto the canvas until a route re-entry.
   *
   * The caller is responsible for the guards — see utils/DocumentSync.js. This
   * assumes the document is clean, not saving, and that nothing holds a
   * reference into the block tree.
   *
   * @param {object} item - an item as returned by the API
   * @return {boolean} whether the swap happened
   */
  @action reloadFromServer = (item) => {
    // amend() mutates what it is given and only understands a string body
    // (it is gated on `item.body.replace`), so hand it a private copy.
    const prepared = {
      ...item,
      body:
        typeof item.body === 'string' ? item.body : JSON.stringify(item.body),
    }

    let parsedBody
    try {
      parsedBody = JSON.parse(prepared.body)
    } catch (e) {
      parsedBody = null
    }

    // amend() dereferences body.columns[0].items with no guard, where load()
    // defends itself. Refusing a thin or legacy body is safer than throwing
    // half way through the swap.
    if (
      !parsedBody ||
      !parsedBody.settings ||
      !get(parsedBody, 'columns[0].items')
    ) {
      return false
    }

    // A stale `false` from a numeric input freezes both undo recording and
    // autosave for the rest of the session, and the document it referred to is
    // about to be gone.
    this.setIsValid(true)
    this.error = undefined

    // Taken before anything changes, so a swap that throws half way can be put
    // back. Otherwise the tab would hold a mix of both documents, read as
    // dirty, and the next autosave would PUT that mix.
    const before = this.serialize()

    try {
      // amend() merges settings where load() replaces them, so a setting the
      // other person removed would otherwise linger. setDefault() with no
      // argument keeps the type defaults and clears the document's own values.
      this.settings.setDefault()

      this.amend(prepared)
    } catch (e) {
      const beforeBody = JSON.parse(before.body)
      this.settings.setDefault()
      this.columns[0].amend(beforeBody.columns[0].items)
      this.settings.set(beforeBody.settings)
      this.setSubject(before.subject)
      this.setPreHeader(before.pre_header)
      throw e
    }

    // Fields amend() does not carry but serialize() does — leaving them stale
    // would keep the fingerprint from ever converging, and the tab would
    // reload on a loop.
    this.language = item.language || 'en'
    if (item.thumbnail_url) {
      this.thumbnailUrl = item.thumbnail_url
    }
    if (item.preview_url) {
      this.previewUrl = item.preview_url
    }
    this.setShouldShowAds(!!item.should_show_ads)

    this.setIsSaving(false)
    // Last, so serialize() sees the finished document. Without this the tab
    // reads as dirty and the next autosave tick PUTs the content it just
    // fetched straight back.
    this.setVersionHash(hash(this.serialize()))
    this.userStartHash = null
    this.setRemoteFingerprint(fingerprint(item))
    this.setServerUpdated(item.updated)
    this.clearRemoteChange()
    // The error was about a document that is no longer on screen.
    this.clearSaveError()
    this.setLastSaveFailed(false)
    this.clearSaveConflict()

    return true
  }

  @action setSubject = (val) => {
    this.subject = val
  }

  @action setPreHeader = (val) => {
    this.pre_header = val
  }

  @action isSubjectValid = (val) => {
    if (!val) return false
    const trimmedValue = val.trim()
    return trimmedValue && trimmedValue.length > 0
  }

  @action setIsSaving = (val) => {
    this.isSaving = val
  }

  @action setError = (error) => {
    this.error = error
  }

  @action clear = () => {
    this.id = undefined
    this.type = undefined
    this.kind = undefined
    this.subKind = undefined
    this.wizardKind = undefined
    this.itemHash = undefined
    this.setSubject(undefined)
    if (this.columns && this.columns.length && this.columns[0]) {
      this.columns[0].clear()
    }
    this.settings.setDefault()
    this.setShouldShowAds(false)
    this.setVersionHash('')

    this.name = undefined
    this.list = undefined
    this.sender = undefined
    this.status = undefined
    this.template = undefined

    this.form_delay = undefined
    this.form_page_filter = undefined

    this.page_title = undefined

    this.mail_subject = undefined
    this.setPreHeader(undefined)

    this.url = undefined
    this.error = undefined

    this.questions = undefined

    storage.clear()
  }

  @action setShouldShowAds = (shouldShowAds) => {
    this.shouldShowAds = shouldShowAds
  }

  @action setVersionHash = (hash) => {
    this.versionHash = hash
  }

  // Called on the user's mousedown, touchstart, scroll and keydown.
  markUserActivity = () => {
    if (this.userStartHash === null) {
      this.userStartHash = hash(this.serialize())
    }
  }

  // Unsaved edits the user made, as opposed to the editor's own rewrites on
  // load (FE-1485). Read synchronously by the leave prompt, so it compares the
  // document now rather than waiting for History's delayed record of it.
  hasUnsavedUserChanges = () => {
    if (this.userStartHash === null || this.isSaved) {
      return false
    }
    return hash(this.serialize()) !== this.userStartHash
  }

  @action setRemoteFingerprint = (value) => {
    this.remoteFingerprint = value
  }

  @action setServerUpdated = (value) => {
    this.serverUpdated = value || ''
  }

  @action setSaveConflict = (item) => {
    this.saveConflict = item
  }

  @action clearSaveConflict = () => {
    this.saveConflict = null
  }

  // Re-arms the warning: a further remote change after a dismissal is news again.
  @action setRemoteChangePending = (value) => {
    this.remoteChangePending = true
    this.remoteChangeDismissed = false
    this.remoteChangeFingerprint = value || ''
  }

  @action clearRemoteChange = () => {
    this.remoteChangePending = false
    this.remoteChangeDismissed = false
    this.remoteChangeFingerprint = ''
  }

  @action dismissRemoteChange = () => {
    this.remoteChangeDismissed = true
  }

  // Shown only while the tab still holds the edits that would overwrite someone
  // else's save. Once this tab saves, its write has already won and the warning
  // is stale, so isSaved clears it.
  @computed get showRemoteChangeWarning() {
    return (
      this.remoteChangePending &&
      !this.remoteChangeDismissed &&
      !this.isSaved &&
      // Both use the same banner slot, and the save error matters more.
      !this.saveError
    )
  }

  @action setSaveError = (value) => {
    this.saveError = value
  }

  @action clearSaveError = () => {
    this.saveError = null
  }

  @action setLastSaveFailed = (value) => {
    this.lastSaveFailed = value
  }

  // Hides the banner only. Autosave stays as isAutosaveBlocked decides.
  @action dismissSaveError = () => {
    if (this.saveError) {
      this.saveError = { ...this.saveError, dismissed: true }
    }
  }

  /**
   * Whether autosave should hold off because of a rejected save. A validation
   * error is retried once the user has changed the document; the others can't
   * be fixed in the editor, so autosave stops until the page is reloaded. A
   * manual save is always allowed.
   */
  @computed get isAutosaveBlocked() {
    if (!this.saveError) {
      return false
    }
    if (this.saveError.kind !== SAVE_ERROR_KINDS.VALIDATION) {
      return true
    }
    return this.saveError.versionHash === hash(this.serialize())
  }

  @action setIsValid = (isValid) => {
    this.isValid = isValid
  }

  @action setViewMode = (mode) => {
    this.viewMode = mode
  }

  @computed get isSaved() {
    const newHash = hash(this.serialize())
    return this.versionHash === newHash
  }

  @computed get itemColors() {
    const s = this.settings.values
    let colors = [
      s.backgroundColor,
      s.contentColor,
      s.normalText_color,
      s.headings_color,
      s.link_color,
      s.divider_color,
      s.button_textColor,
      s.button_backgroundColor,
      s.button_borderColor,
      s.section_borderColor,
      s.themeColor,
    ]

    if (
      this.columns &&
      this.columns.length &&
      this.columns[0] &&
      this.columns[0].allColors
    ) {
      colors = colors.concat(this.columns[0].allColors)
    }

    const blocks = this.getBlocks()

    blocks.forEach((block) => {
      if (block.allColors) {
        colors = colors.concat(block.allColors)
      }
    })

    return Array.from(
      new Set(
        colors
          .filter(Boolean)
          .map((hex) => {
            let rgba
            try {
              rgba = hexToRGBA(hex)
            } catch (e) {
              captureException(e)
              return null
            }
            return rgba
          })
          .filter(Boolean)
          .map((color) => `${rgbObjectToHex(color, true)}`)
      )
    )
      .sort()
      .slice(0, 20)
  }

  // Client-side link-quality issues across all blocks (FE-1136). Each entry is
  // one bad link, carrying the block id so the panel can scroll to / select it.
  @computed get linkIssues() {
    return this.getBlocks().flatMap((block) =>
      extractLinks(block)
        .map((link) => ({
          link,
          ruleKey: classifyLink(link.value, { allowEmpty: link.allowEmpty }),
        }))
        .filter((entry) => entry.ruleKey)
        .map((entry) => ({
          blockId: block.id,
          blockType: block.type,
          labelKey: entry.link.labelKey,
          anchorText: entry.link.anchorText,
          anchorTextKey: entry.link.anchorTextKey,
          url: entry.link.value,
          ruleKey: entry.ruleKey,
        }))
    )
  }

  @computed get missingAttrs() {
    const toBeChecked =
      this.kind === ITEM_TYPE_MAIL || this.kind === ITEM_TYPE_RESPONDER
        ? [VIEW_IN_BROWSER_MARKUP, UNSUBSCRIBE_MARKUP]
        : this.itemHash && this.kind === ITEM_TYPE_CONFIRMATION_MAIL
        ? [CONFIRM_MARKUP]
        : []

    // Get all blocks that can contain links
    const blocks = this.getBlocks().filter(
      (item) =>
        item.type === BLOCK_TYPES.TEXT ||
        item.type === BLOCK_TYPES.BUTTON ||
        item.type === BLOCK_TYPES.SIMPLE_BUTTON ||
        item.type === BLOCK_TYPES.CONFIRM_BUTTON
    )

    return toBeChecked
      .map((markup) => {
        let hasValidOccurrence = false
        let hasInvalidOccurrence = false

        blocks.forEach((item) => {
          if (item.type === BLOCK_TYPES.CONFIRM_BUTTON) {
            // Confirm button always contains confirmation link
            if (markup === CONFIRM_MARKUP) hasValidOccurrence = true
            return
          }

          if (
            item.type === BLOCK_TYPES.BUTTON ||
            item.type === BLOCK_TYPES.SIMPLE_BUTTON
          ) {
            // Button URL is always a link context
            const url = item.settings?.values?.url || ''
            if (url === markup) {
              hasValidOccurrence = true
            } else if (url.includes(markup)) {
              // Placeholder with extra content (e.g., ##unsubscribe##/path)
              hasInvalidOccurrence = true
            }
            return
          }

          // TEXT block - validate placeholders in href attributes
          // Plain text placeholders outside href are allowed
          const htmlText = item.htmlText || ''

          // Extract href values and check for valid placeholder usage
          const hrefRegex = /href=["']([^"']+)["']/gi
          let match
          while ((match = hrefRegex.exec(htmlText)) !== null) {
            const hrefValue = match[1]
            if (hrefValue === markup) {
              hasValidOccurrence = true
            } else if (hrefValue.includes(markup)) {
              // Placeholder with extra content in href - invalid
              hasInvalidOccurrence = true
            }
          }
        })

        // Valid only if at least one valid occurrence AND no invalid occurrences
        return hasValidOccurrence && !hasInvalidOccurrence
      })
      .map((isValid, index) => (!isValid ? toBeChecked[index] : ''))
      .filter((markup) => markup)
  }

  @computed get hasTextWithoutStyles() {
    return (
      this.getBlocks().filter(
        (item) =>
          item.type === BLOCK_TYPES.TEXT &&
          (item.htmlText.startsWith('<p>') ||
            item.htmlText.startsWith('<h1>') ||
            item.htmlText.startsWith('<h2>') ||
            item.htmlText.startsWith('<h3>') ||
            item.htmlText.startsWith('<h4>') ||
            item.htmlText.startsWith('<h5>') ||
            item.htmlText.startsWith('<h6>') ||
            item.htmlText.startsWith('<ol>') ||
            item.htmlText.startsWith('<ul>'))
      ).length > 0
    )
  }

  @computed get hasSections() {
    return (
      this.columns &&
      this.columns.length > 0 &&
      this.columns[0] &&
      this.columns[0].items &&
      this.columns[0].items.length > 0
    )
  }

  @computed get numberOfFormBlocks() {
    const formBlocks = this.getBlocks().filter(
      (item) => item.type === BLOCK_TYPES.FORM
    )

    return formBlocks.length
  }

  @computed get hasFieldBlocks() {
    const blocks = this.getBlocks().filter(
      (item) => item.type === BLOCK_TYPES.FIELD
    )

    return blocks.length > 0
  }

  @computed get hasSubmitButton() {
    const blocks = this.getBlocks().filter(
      (item) => item.type === BLOCK_TYPES.SUBMIT_BUTTON
    )

    return blocks.length > 0
  }

  @computed get hasConfirmButtonBlock() {
    const formBlocks = this.getBlocks().filter(
      (item) => item.type === BLOCK_TYPES.CONFIRM_BUTTON
    )

    return formBlocks.length > 0
  }

  @computed get popupWidth() {
    if (
      this.kind !== ITEM_TYPE_FORM ||
      !this.columns ||
      (this.columns && !this.columns.length) ||
      (this.columns && this.columns.length && !this.columns[0]) ||
      (this.columns &&
        this.columns.length &&
        this.columns[0] &&
        !this.columns[0].items) ||
      (this.columns &&
        this.columns.length &&
        this.columns[0] &&
        this.columns[0].items &&
        this.columns[0].items.length &&
        !this.columns[0].items[0])
    ) {
      return undefined
    }
    const sectionModel = this.columns[0].items[0]
    if (
      sectionModel.settings.values.bgImagePosition ===
        FORMCOL_SECTION_BG_IMAGE_POSITION_TYPES[0].value ||
      sectionModel.settings.values.bgImagePosition ===
        FORMCOL_SECTION_BG_IMAGE_POSITION_TYPES[2].value
    ) {
      return this.settings.values.popup_width
    } else if (
      sectionModel.settings.values.bgImagePosition ===
        FORMCOL_SECTION_BG_IMAGE_POSITION_TYPES[1].value ||
      sectionModel.settings.values.bgImagePosition ===
        FORMCOL_SECTION_BG_IMAGE_POSITION_TYPES[3].value
    ) {
      return this.settings.values.popup_width_alternate
    }
    return undefined
  }

  @computed get isEmail() {
    return (
      this.kind === ITEM_TYPE_CONFIRMATION_MAIL ||
      this.kind === ITEM_TYPE_MAIL ||
      this.kind === ITEM_TYPE_RESPONDER
    )
  }

  /**
   * The newer server copy that this save must not overwrite, or null when the
   * save can go ahead. See FE-1441.
   *
   * Only newsletters and autoresponders are checked, the same two FE-1354
   * polls (getSilentFetchMethodByType returns nothing for the rest). The check
   * fails open: when the server's copy can't be read, the save goes ahead as it
   * did before this check existed.
   *
   * A newer `updated` alone doesn't block. `Mail.updated` is `auto_now`, so it
   * also moves on writes that don't touch the content (unscheduling, a
   * responder queue picking up a new subject). Those leave the content as this
   * tab loaded it, or already the same as this tab's, and saving over them
   * loses nothing.
   *
   * @return {Promise<object|null>}
   */
  findSaveConflict = async () => {
    const load = ganApi.getSilentFetchMethodByType(this.kind)
    if (!load || !this.id || !this.serverUpdated) {
      return null
    }

    const server = await load(this.id)
    if (!server || !server.updated) {
      return null
    }
    if (!isNewerServerVersion(server.updated, this.serverUpdated)) {
      return null
    }
    if (
      fingerprint(server) === this.remoteFingerprint ||
      matchesLocal(server, this.serialize())
    ) {
      this.setServerUpdated(server.updated)
      return null
    }
    return server
  }

  /**
   * @param {boolean} force - save even when nothing changed locally
   */
  save = (force = false) => {
    this.setIsSaving(true)

    if (typeof tinymce !== 'undefined') {
      tinymce.triggerSave()
    }

    const item = this.serialize()
    const makeRequest = ganApi.getUpdateMethodByType(this.wizardKind, this.kind)

    const newHash = hash(item)

    if (!force && (this.versionHash === newHash || this.kind === '')) {
      this.setIsSaving(false)
      // Nothing unsaved, so nothing to lose by leaving.
      this.setLastSaveFailed(false)
      return Promise.resolve(item)
    }

    // A newer server version is waiting for the user to load it. Until they do,
    // nothing saves, or autosave would retry the same blocked save every minute.
    if (this.saveConflict) {
      this.setIsSaving(false)
      return Promise.resolve(item)
    }

    storage.saveItem(item)
    return this.findSaveConflict()
      .then((conflict) => {
        if (conflict) {
          this.setSaveConflict(conflict)
          // Skips the PUT. Not ok with no status: the save counts as failed,
          // but there is no 4xx to show, the save conflict dialog says why.
          return { ok: false, status: null, data: null }
        }
        return makeRequest(item)
      })
      .then(({ ok, status, data: response }) => {
        this.setIsSaving(false)
        if (!ok) {
          this.setLastSaveFailed(true)
          // 5xx and network failures open the reconnect dialog and a 401 goes
          // to login (api/handleResponse.js); only a 4xx is shown here.
          if (status >= 400 && status < 500 && status !== 401) {
            this.setSaveError({
              ...describeSaveError({
                status,
                data: response,
                itemKind: this.kind,
              }),
              versionHash: newHash,
            })
          }
          return item
        }
        if (
          !get(response, 'id') &&
          !get(response, 'hash') &&
          !get(response, this.getBodyFieldName())
        ) {
          // Not recognisably saved: the tab stays dirty, so treat it as failed.
          this.setLastSaveFailed(true)
          return item
        }

        this.setVersionHash(newHash)
        // Undoing back past a saved edit is a change of its own.
        if (this.userStartHash !== null) {
          this.userStartHash = newHash
        }
        // Re-baseline from what the server now holds, or the next background
        // poll reads this tab's own write as somebody else's change.
        //
        // Only when the response actually carries the body, though: the guard
        // above admits any response with an id, and fingerprinting a bodyless
        // one would record a hash over an empty document. The next poll would
        // then "discover" a change and reload the tab onto its own save. Better
        // to leave the old baseline and let the poll resolve it. See FE-1354.
        if (get(response, 'body')) {
          this.setRemoteFingerprint(fingerprint(response))
        }
        // The version this tab now holds is the one it just wrote, so the next
        // save checks against it rather than the one it loaded. See FE-1441.
        if (get(response, 'updated')) {
          this.setServerUpdated(response.updated)
        }
        this.clearRemoteChange()
        this.clearSaveError()
        this.setLastSaveFailed(false)
        return response
      })
      .catch(async (error) => {
        console.error(error)

        if (error instanceof Response) {
          const text = await error.text().catch(() => 'Unable to parse body')
          captureException(error, {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            responseBody: text,
          })
        } else {
          captureException(error)
        }

        this.setIsSaving(false)
        this.setLastSaveFailed(true)
        return item
      })
  }

  getBlocks = () => {
    if (!this.hasSections) {
      return []
    }

    const sections = this.columns[0].items

    if (!sections || !sections.length) return []

    const columns = sections
      .map((item) => item.columns)
      .reduce((acc, columns) => acc.concat(columns), [])

    if (!columns || !columns.length) return []

    const blocks = columns
      .map((column) =>
        column && column.items && column.items.length
          ? column.items.slice()
          : []
      )
      .reduce((acc, items) => acc.concat(items), [])
    return blocks
  }

  getContent = () => {
    const checkBlocks = this.getBlocks()
      .filter(
        (item) =>
          item.type === BLOCK_TYPES.TEXT ||
          item.type === BLOCK_TYPES.BUTTON ||
          item.type === BLOCK_TYPES.SIMPLE_BUTTON ||
          item.type === BLOCK_TYPES.CONFIRM_BUTTON
      )
      .map((item) => {
        if (item.type === BLOCK_TYPES.CONFIRM_BUTTON) {
          return CONFIRM_MARKUP
        }
        return item.htmlText || item.settings.values.url || ''
      })

    if (!checkBlocks || !checkBlocks.length) return ''

    const content = checkBlocks.reduce((acc, text) => acc + text, '')

    return content
  }

  getFonts = () => {
    const mailFonts = [
      {
        name: this.settings.values.headings_fontFamily,
        value: this.settings.values.headings_fontFamily,
      },
      {
        name: this.settings.values.normalText_fontFamily,
        value: this.settings.values.normalText_fontFamily,
      },
    ].map((font) => {
      const name = font.name.split(',')[0] || font.name
      return {
        name: name,
        value: font.value,
      }
    })
    const contentFonts = this.getContent().match(/<(span)(.*?)>/g)
    const contentFontsPrepared = (contentFonts || [])
      .map((match) => {
        let font = false
        if (match.includes('font-family')) {
          const r = /font-family:\s?'?(?<font>[\w\s]+)'?;?/g
          const result = r.exec(match)
          font =
            result && result.groups && result.groups.font
              ? result.groups.font
              : false
        }
        if (webSafeFonts.find((item) => item.name === font)) {
          return false
        }
        return font
      })
      .filter(Boolean)
      .map((font) => {
        const name = font.split(',')[0] || font
        return {
          name: name,
          value: font,
        }
      })
    return [...mailFonts, ...contentFontsPrepared]
  }

  serialize() {
    if (this.id) {
      return {
        type: MAIL_TYPE_UPDATED,
        id: this.id,
        url: this.url,
        subject: this.subject,
        pre_header: this.pre_header,
        body: JSON.stringify({
          columns: this.columns.map((column) => column.serialize()),
          settings: this.settings.serialize(),
        }),
        thumbnail_url: this.thumbnailUrl,
        preview_url: this.previewUrl,
        language: this.language,
      }
    }
    if (this.itemHash && this.kind === ITEM_TYPE_FORM) {
      return {
        kind: this.kind,
        hash: this.itemHash,
        name: this.name,
        list: this.list,
        sender: this.sender,
        status: this.status,
        template: this.template,
        [`${`${this.kind}`.toLowerCase()}_body`]: JSON.stringify({
          columns: this.columns.map((column) => column.serialize()),
          settings: this.settings.serialize(),
        }),
        [`${`${this.kind}`.toLowerCase()}_delay`]: this.form_delay,
        [`${`${this.kind}`.toLowerCase()}_page_filter`]: this.form_page_filter,
        [`${`${this.kind}`.toLowerCase()}_type`]: this.form_type,
        [`${`${this.kind}`.toLowerCase()}_on_scroll`]: this.form_on_scroll,
        [`${`${this.kind}`.toLowerCase()}_trigger`]: this.form_trigger,
        language: this.language,
      }
    }
    if (this.itemHash && this.kind === ITEM_TYPE_CONFIRMATION_MAIL) {
      return {
        kind: this.kind,
        hash: this.itemHash,
        name: this.name,
        list: this.list,
        sender: this.sender,
        status: this.status,
        template: this.template,
        [`mail_body`]: JSON.stringify({
          columns: this.columns.map((column) => column.serialize()),
          settings: this.settings.serialize(),
        }),
        [`mail_subject`]: this.subject,
        pre_header: this.pre_header,
        language: this.language,
        design_settings:
          this.wizardKind === WIZARD_TYPE_PAGE
            ? JSON.stringify(this.settings.serialize())
            : undefined,
      }
    }
    if (this.itemHash && this.kind === ITEM_TYPE_LANDING_PAGE) {
      return {
        kind: this.kind,
        hash: this.itemHash,
        name: this.name,
        list: this.list,
        sender: this.sender,
        status: this.status,
        template: this.template,
        [`form_body`]: JSON.stringify({
          columns: this.columns.map((column) => column.serialize()),
          settings: this.settings.serialize(),
        }),
        [`form_page_title`]: this.subject,
        language: this.language,
        design_settings: JSON.stringify(this.settings.serialize()),
      }
    }
    if (this.itemHash && this.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS) {
      return {
        kind: this.kind,
        hash: this.itemHash,
        name: this.name,
        list: this.list,
        sender: this.sender,
        status: this.status,
        template: this.template,
        [`success_page_body`]: JSON.stringify({
          columns: this.columns.map((column) => column.serialize()),
          settings: this.settings.serialize(),
        }),
        [`success_page_title`]: this.subject,
        language: this.language,
        design_settings: JSON.stringify(this.settings.serialize()),
      }
    }
    if (this.itemHash && this.kind === ITEM_TYPE_SURVEY) {
      const columns = this.columns.map((column) => column.serialize())
      const items = get(columns, '[0].items[0].columns[0].items')
      const questions = (items || [])
        .filter((item) => item.type === BLOCK_TYPES.FIELD)
        .map((item, index) => {
          const choices = (item.settings.choices || []).map(
            (choice, choiseIndex) => {
              return {
                name: choice.label,
                value: choice.value,
                order: choiseIndex,
              }
            }
          )
          return {
            input_name: item.inputName,
            text: item.settings.question,
            help_text: item.settings.helpText,
            order: index,
            is_required: item.settings.isRequired,
            type: getServerFieldType(item),
            settings: item.settings,
            choices,
          }
        })
      return {
        kind: this.kind,
        hash: this.itemHash,
        name: this.subject,
        status: this.status,
        template: this.template,
        [`form_body`]: JSON.stringify({
          columns: columns,
          settings: this.settings.serialize(),
        }),
        language: this.language,
        questions: questions,
        design_settings: JSON.stringify(this.settings.serialize()),
      }
    }
    if (this.itemHash && this.kind === ITEM_TYPE_SURVEY_THANK_YOU) {
      return {
        kind: this.kind,
        hash: this.itemHash,
        name: this.subject,
        status: this.status,
        template: this.template,
        [`thank_you_page_body`]: JSON.stringify({
          columns: this.columns.map((column) => column.serialize()),
          settings: this.settings.serialize(),
        }),
        language: this.language,
        design_settings: JSON.stringify(this.settings.serialize()),
      }
    }
    if (this.itemHash && this.kind === ITEM_TYPE_PAGE) {
      return {
        kind: this.kind,
        hash: this.itemHash,
        name: this.name,
        list: this.list,
        sender: this.sender,
        status: this.status,
        template: this.template,
        [`${`${this.kind}`.toLowerCase()}_body`]: JSON.stringify({
          columns: this.columns.map((column) => column.serialize()),
          settings: this.settings.serialize(),
        }),
        [`${`${this.kind}`.toLowerCase()}_title`]: this.subject,
        language: this.language,
        design_settings:
          this.wizardKind === WIZARD_TYPE_PAGE
            ? JSON.stringify(this.settings.serialize())
            : undefined,
      }
    }
    return {}
  }
}

export const NULL = new EditorModel({})

export default EditorModel
